import { useState, useEffect, useRef } from 'react';
import { useFaceLandmarker } from './useFaceLandmarker';
import { getYawRatio, getPitchRatio, getGazeRatios } from './proctoringDetector';
import { submitViolation, submitSnapshot } from '../../../../services/proctoringService';
import { toast } from 'sonner';

const VIOLATION_COOLDOWN_MS = 30000; // 30 seconds between logs
const SNAPSHOT_INTERVAL_MS = 600000; // 10 minutes periodic re-verification

export const useLiveProctoring = ({ sessionId, isActive, preCalibratedBaselines = null }) => {
  const { faceLandmarker, isLoading: modelsLoading, error: modelError } = useFaceLandmarker();

  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isCalibrated, setIsCalibrated] = useState(!!preCalibratedBaselines);
  const [calibrationProgress, setCalibrationProgress] = useState(preCalibratedBaselines ? 100 : 0);
  const [activeWarning, setActiveWarning] = useState(null); // 'frame_exit' | 'multiple_faces' | 'head_turn' | 'eye_wandering'

  // Refs for tracking frames and thresholds
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameIdRef = useRef(null);
  const lastDetectionTimeRef = useRef(0);
  const snapshotIntervalRef = useRef(null);

  // Baselines for math detector
  const baselineYawRef = useRef(preCalibratedBaselines?.yaw ?? 0.5);
  const baselinePitchRef = useRef(preCalibratedBaselines?.pitch ?? 0.5);
  const baselineGazeLRef = useRef(preCalibratedBaselines?.gazeL ?? 0.5);
  const baselineGazeRRef = useRef(preCalibratedBaselines?.gazeR ?? 0.5);

  // Calibration accumulator
  const calibrationFramesRef = useRef([]);

  // Consecutive frames counts (at 5 FPS: 5 frames = 1 second)
  const exitFramesRef = useRef(0);      // Needs 3s = 15 frames
  const multiFaceFramesRef = useRef(0); // Needs 2s = 10 frames
  const headTurnFramesRef = useRef(0);  // Needs 3s = 15 frames
  const gazeWanderFramesRef = useRef(0); // Needs 3s = 15 frames

  // Cooldown timestamps
  const lastViolationTimeRef = useRef({
    frame_exit: 0,
    multiple_faces: 0,
    head_turn: 0,
    eye_wandering: 0
  });

  // Get user camera stream
  useEffect(() => {
    if (!isActive) return;

    const startCamera = async () => {
      try {
        setCameraError(null);
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 320 }, height: { ideal: 240 } },
          audio: false
        });
        streamRef.current = mediaStream;
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error("Proctoring camera access denied:", err);
        setCameraError("Camera access is required for proctoring.");
        toast.error("Proctoring Camera Access Denied. Please enable camera permissions.");
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isActive]);

  // Periodic re-verification snapshot logic
  useEffect(() => {
    if (!isActive || !sessionId || !stream) return;

    const triggerSnapshot = async () => {
      if (!videoRef.current) return;
      const video = videoRef.current;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 240;
        const ctx = canvas.getContext('2d');

        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            const res = await submitSnapshot(sessionId, blob);
            if (res.verified) {
              toast.success(`Identity Re-verified (${res.similarity?.toFixed(1)}% match)`);
            }
          } catch (err) {
            console.error("Periodic snapshot re-verification failed:", err);
            toast.error("Identity mismatch detected during check! Make sure you are facing the camera.");
          }
        }, 'image/jpeg', 0.85);

      } catch (e) {
        console.error("Failed to capture periodic snapshot:", e);
      }
    };

    snapshotIntervalRef.current = setInterval(triggerSnapshot, SNAPSHOT_INTERVAL_MS);
    const firstCheckTimeout = setTimeout(triggerSnapshot, 60000);

    return () => {
      if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
      clearTimeout(firstCheckTimeout);
    };
  }, [isActive, sessionId, stream]);

  // Main tracking / detection frame loop
  useEffect(() => {
    if (!faceLandmarker || !stream || !isActive || !sessionId) return;

    console.log("[Proctoring] Active tracking loop mounting. isActive:", isActive, "hasStream:", !!stream, "hasLandmarker:", !!faceLandmarker, "isCalibrated:", isCalibrated, "sessionId:", sessionId);

    const processFrame = () => {
      if (!videoRef.current) {
        frameIdRef.current = requestAnimationFrame(processFrame);
        return;
      }

      const video = videoRef.current;
      const now = performance.now();

      if (now - lastDetectionTimeRef.current >= 200) {
        lastDetectionTimeRef.current = now;

        if (video.readyState >= 2) {
          const results = faceLandmarker.detectForVideo(video, now);
          handleDetections(results);
        }
      }

      frameIdRef.current = requestAnimationFrame(processFrame);
    };

    frameIdRef.current = requestAnimationFrame(processFrame);

    return () => {
      console.log("[Proctoring] Active tracking loop cleaning up/unmounting.");
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [faceLandmarker, stream, isActive, isCalibrated, sessionId]);

  const detectFramesRef = useRef(0);

  const handleDetections = (results) => {
    const faces = results.faceLandmarks || [];

    if (faces.length === 0) {
      exitFramesRef.current += 1;
      multiFaceFramesRef.current = 0;
      headTurnFramesRef.current = 0;
      gazeWanderFramesRef.current = 0;

      console.log("[Proctoring] Face out of frame! exitFramesRef.current:", exitFramesRef.current);

      if (exitFramesRef.current >= 15) { // 3 seconds
        triggerViolation('frame_exit', "Face not visible in camera frame");
      }
      return;
    }

    exitFramesRef.current = 0;
    const landmarks = faces[0];

    detectFramesRef.current++;
    if (detectFramesRef.current % 15 === 0) {
      console.log("[Proctoring] Face detected in frame. Active. isCalibrated:", isCalibrated);
    }

    if (faces.length > 1) {
      multiFaceFramesRef.current += 1;
      headTurnFramesRef.current = 0;
      gazeWanderFramesRef.current = 0;

      if (multiFaceFramesRef.current >= 10) { // 2 seconds
        triggerViolation('multiple_faces', "Multiple faces detected in frame");
      }
      return;
    }

    multiFaceFramesRef.current = 0;

    if (!isCalibrated) {
      accumulateCalibration(landmarks);
      return;
    }

    const yaw = getYawRatio(landmarks);
    const pitch = getPitchRatio(landmarks);
    const { gazeL, gazeR, vGazeL, vGazeR } = getGazeRatios(landmarks);

    const yawDev = yaw - baselineYawRef.current;
    const pitchDev = pitch - baselinePitchRef.current;
    const gazeLDev = gazeL - baselineGazeLRef.current;
    const gazeRDev = gazeR - baselineGazeRRef.current;
    const vGazeLDev = vGazeL - baselineGazeLRef.current;
    const vGazeRDev = vGazeR - baselineGazeRRef.current;

    const isHeadTurned = Math.abs(yawDev) > 0.15 || Math.abs(pitchDev) > 0.12;
    const isGazeWandering = (Math.abs(gazeLDev) + Math.abs(gazeRDev)) / 2 > 0.08 ||
      (Math.abs(vGazeLDev) + Math.abs(vGazeRDev)) / 2 > 0.1;

    if (isHeadTurned) {
      headTurnFramesRef.current += 1;
      gazeWanderFramesRef.current = 0;

      if (headTurnFramesRef.current >= 15) { // 3 seconds
        triggerViolation('head_turn', "Please keep looking at the screen");
      }
    } else if (isGazeWandering) {
      gazeWanderFramesRef.current += 1;
      headTurnFramesRef.current = 0;

      if (gazeWanderFramesRef.current >= 15) { // 3 seconds
        triggerViolation('eye_wandering', "Keep your eyes on the examination sheet");
      }
    } else {
      headTurnFramesRef.current = 0;
      gazeWanderFramesRef.current = 0;
      setActiveWarning(null);
    }
  };

  const accumulateCalibration = (landmarks) => {
    const list = calibrationFramesRef.current;
    const yaw = getYawRatio(landmarks);
    const pitch = getPitchRatio(landmarks);
    const { gazeL, gazeR } = getGazeRatios(landmarks);

    list.push({ yaw, pitch, gazeL, gazeR });
    setCalibrationProgress(Math.min(100, Math.floor((list.length / 15) * 100)));

    if (list.length >= 15) {
      const avgYaw = list.reduce((sum, f) => sum + f.yaw, 0) / list.length;
      const avgPitch = list.reduce((sum, f) => sum + f.pitch, 0) / list.length;
      const avgGazeL = list.reduce((sum, f) => sum + f.gazeL, 0) / list.length;
      const avgGazeR = list.reduce((sum, f) => sum + f.gazeR, 0) / list.length;

      baselineYawRef.current = avgYaw;
      baselinePitchRef.current = avgPitch;
      baselineGazeLRef.current = avgGazeL;
      baselineGazeRRef.current = avgGazeR;

      setIsCalibrated(true);
      toast.success("Camera calibrated! Proctoring active.");
    }
  };

  const triggerViolation = async (type, friendlyMessage) => {
    console.log("[Proctoring] triggerViolation called. type:", type, "friendlyMessage:", friendlyMessage);
    setActiveWarning(type);

    const now = Date.now();
    if (now - lastViolationTimeRef.current[type] < VIOLATION_COOLDOWN_MS) {
      console.log("[Proctoring] Violation throttled because of cooldown window");
      return;
    }

    lastViolationTimeRef.current[type] = now;

    console.log("[Proctoring] Displaying toast notification warning to student");
    toast.error(`Warning: ${friendlyMessage}`, {
      duration: 4000
    });

    if (!videoRef.current) return;
    const video = videoRef.current;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 320;
      canvas.height = video.videoHeight || 240;
      const ctx = canvas.getContext('2d');

      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        try {
          await submitViolation(sessionId, type, {
            message: friendlyMessage,
            timestamp: new Date().toISOString()
          }, blob);
          console.log(`Violation ${type} uploaded successfully.`);
        } catch (err) {
          console.error(`Failed to submit violation ${type}:`, err);
        }
      }, 'image/jpeg', 0.85);

    } catch (e) {
      console.error("Failed to capture violation screenshot:", e);
    }
  };

  return {
    videoRef,
    stream,
    cameraError,
    isCalibrated,
    calibrationProgress,
    activeWarning,
    modelsLoading,
    modelError
  };
};
